import type { NamedSetHref } from '@generalTypes/apiTypes';
import { KovResourcePicker } from '@kathondvla/react-shared-components/src/components';
import { selectIsRoot } from '@newStore/documentUI/documentUISelectors';
import type { EditAsideAccessRights } from '@nodeTypeConfig/configTypes';
import { patchNodeAction } from '@store/actions/documentActions';
import { type AccessRight, accessRights } from '@store/helpers/accessRights';
import { getResourceKey } from '@store/helpers/documentHelpers';
import { escapeRegExp, set } from 'lodash';
import { any } from 'prop-types';
import type React from 'react';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { getIsGlobalDocumentIncludedInProTheme } from '@newStore/documentSpecific/documentSpecificState';
import { useEditHref } from '../../../hooks/UseEditHref';
import { useInitialValue } from '../../../hooks/UseInitialValue';
import {
  selectCurrentSelectedAccessRightsOption,
  selectIsGlobalDocument,
} from './asideAccessRightsSelectors';

import { useTypedSelector } from '../../../hooks/useTypedSelector';
import './AsideAccessRights.scss';

const AsideAccessRights: React.FC<{
  config: EditAsideAccessRights;
  readonly: boolean;
}> = ({ config, readonly }) => {
  const dispatch = useDispatch();
  const editHref = useEditHref();

  const itemValue: NamedSetHref[] | undefined = useInitialValue(config.property);
  const isRoot = useTypedSelector((state) => selectIsRoot(state, editHref));
  const isInherited = !isRoot && !itemValue;
  const isGlobalDocument = useTypedSelector((state) => selectIsGlobalDocument(state));

  const selectedAccessRights: AccessRight = useTypedSelector((state) =>
    selectCurrentSelectedAccessRightsOption(state, editHref, config),
  );

  const isInProTheme = useTypedSelector(
    (state) => state.documentSpecific.isGlobalDocumentIncludedInProTheme,
  );

  useEffect(() => {
    if (isGlobalDocument && isInProTheme === null) {
      dispatch(getIsGlobalDocumentIncludedInProTheme({ href: editHref }));
    }
  }, [isGlobalDocument, editHref, dispatch, isInProTheme]);

  const onChange = (value: AccessRight) => {
    dispatch(patchNodeAction(getResourceKey(editHref), set({}, config.property, value.namedsets)));
  };

  const backToInherited = () => {
    dispatch(patchNodeAction(getResourceKey(editHref), set({}, config.property, undefined)));
  };

  const filterData = (data, str) => {
    if (str.length === 0) {
      return data;
    }
    return data.filter((elm) => {
      const regExp = new RegExp(escapeRegExp(str), 'gi');
      return regExp.test(elm.title) || regExp.test(elm.name);
    });
  };

  const disablePicker =
    readonly || (isGlobalDocument && (isInProTheme === true || isInProTheme === null)); // disable the picker if the document is included in a pro theme OR if the request is still running.

  return (
    <div className={`access-rights${isInherited ? ' inherited' : ''}`}>
      <div className="title">
        <label className="control-label">{config.label}</label>
        {isInherited && <span className="inherited-info">Overgenomen</span>}
        {isInProTheme && isGlobalDocument && <span className="inherited-info">berekend</span>}
        {!isInherited && !isRoot && (
          <span className="inherited-revert" onClick={backToInherited}>
            Overnemen
          </span>
        )}
      </div>

      <KovResourcePicker
        value={selectedAccessRights}
        localData={Object.values(accessRights)}
        display={(i: AccessRight) => i.label}
        onChange={onChange}
        disabled={disablePicker}
        filter={filterData}
        className="access-rights-picker"
      />
      {isGlobalDocument && (
        <div>
          <div className="ng-scope">
            <label className="warning-label ng-binding">
              {`${
                isInProTheme
                  ? 'De afscherming van dit document wordt bepaald door de afscherming van de themapagina(s) waarin het is opgenomen.'
                  : 'Vul dit veld enkel in wanneer je een globaal document rechtstreeks wil delen (bv. in een teaser), en niet via een themapagina. Wanneer je dit document opneemt in een themapagina, dan vervalt deze afscherming.'
              }`}
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

export default AsideAccessRights;

AsideAccessRights.displayName = 'AsideAccessRights';

AsideAccessRights.propTypes = {
  config: any,
  readonly: any,
};
